import { useMutation } from '@apollo/client';
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IconButton,
  PrimaryButton,
  Stack,
} from '@fluentui/react';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { useState } from 'react';
import {
  ApprovalTestResultDelete,
  ApprovalTestResultDeleteVariables,
} from './__generated__/ApprovalTestResultDelete';
import DELETE_TEST from './ApprovalTestResultDelete.graphql';
import GET_APPROVAL_DETAILS from '../../../FlowApproval.graphql';
import { ApprovalTestResults_approvalTestResults_nodes } from './__generated__/ApprovalTestResults';
import { ApprovalTestResult_approvalTestResult } from './__generated__/ApprovalTestResult';
import { EntityDeleteInput } from 'common/types/globalTypes';
import { ActionMessageModal } from 'common/components/ActionMessageModal';
import { EntityAction, EntityType } from 'common/types/utility';

export const DeleteTest: React.FC<{
  approvalTestResult?: ApprovalTestResult_approvalTestResult;
  selectedList?: ApprovalTestResults_approvalTestResults_nodes[];
  onClose?: () => void;
  approvalId: string;
}> = ({ selectedList, approvalTestResult, onClose, approvalId }) => {
  const [showDialog, setShowDialog] = useState(false);

  const [DeleteTest] = useMutation<
    ApprovalTestResultDelete,
    ApprovalTestResultDeleteVariables
  >(DELETE_TEST);
  let entityDelete: EntityDeleteInput[];
  if (selectedList) {
    entityDelete = selectedList
      .filter((item) => item._isDeletable)
      .map((item) => ({
        id: item.id!,
        rowTimestamp: item._rowTimestamp!,
      }));
  }
  if (approvalTestResult) {
    entityDelete = [
      {
        id: approvalTestResult.id!,
        rowTimestamp: approvalTestResult._rowTimestamp!,
      },
    ];
  }
  const onDelete = async () => {
    const { errors } = await DeleteTest({
      variables: {
        input: {
          entityDelete,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_APPROVAL_DETAILS,
          variables: {
            id: approvalId!,
          },
        },
      ],
      update(cache) {
        entityDelete.forEach(({ id }) => {
          const cacheId = cache.identify({
            __typename: 'ApprovalTestResult',
            id: id,
          });

          if (cacheId) {
            cache.evict({ id: cacheId });
          }
        });

        cache.gc();
      },
    });
    if (errors?.length) {
      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    } else {
      setShowDialog(false);

      showCustomToast({
        content: 'Test case deleted successfully',
        appearance: 'success',
      });
      onClose?.();
    }
  };
  return (
    <ActionMessageModal
      visible
      entityType={EntityType.TestCase}
      disabled={
        selectedList
          ? selectedList.length === 0 ||
            selectedList.every((item) => !item._isDeletable)
          : !approvalTestResult?._isDeletable
      }
      action={EntityAction.Remove}
      multiple={
        selectedList
          ? {
              validCount: selectedList.filter(
                (selected) => selected._isDeletable
              ).length,
              invalidNames: selectedList
                .filter((selected) => !selected._isDeletable)
                .map((cannotDelete) => cannotDelete.comment || ''),
            }
          : undefined
      }
      onConfirm={onDelete}
    />
  );
};
